import React, { useState, useEffect } from 'react';
import {
  Card,
  Select,
  Switch,
  Button,
  Typography,
  Row,
  Col,
  Skeleton,
} from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import TemplateSelectDropdown from './Settings.TemplateDropdown';
import {
  useGetPractitionerSettings,
  useUpdatePractitionerSettings,
} from 'src/hooks/settings';
import { showToastSuccess } from 'src/utilities/toast';

const { Text, Title } = Typography;
const { Option } = Select;

// Define constants for OP Field Mapping
export const DOCUMENT_TYPE_CHIEF_COMPLAINT = 'chief_complaint';
export const DOCUMENT_TYPE_HPI = 'hpi';
export const DOCUMENT_TYPE_REVIEW_OF_SYSTEMS = 'review_of_systems';
export const DOCUMENT_TYPE_PHYSICAL_EXAM = 'physical_exam';
export const DOCUMENT_TYPE_ASSESSMENT = 'assessment';
export const DOCUMENT_TYPE_PLAN = 'plan';
export const DOCUMENT_TYPE_SUBMIT_PATIENT_INSTRUCTIONS = 'patient_instructions';

export const OP_FIELD_MAPPING = {
  [DOCUMENT_TYPE_CHIEF_COMPLAINT]: 'Chief Complaint',
  [DOCUMENT_TYPE_HPI]: 'HPI',
  [DOCUMENT_TYPE_REVIEW_OF_SYSTEMS]: 'FreeformRos',
  [DOCUMENT_TYPE_PHYSICAL_EXAM]: 'NarrativeExam',
  [DOCUMENT_TYPE_ASSESSMENT]: 'NcAssessment',
  [DOCUMENT_TYPE_PLAN]: 'NCPlan',
  [DOCUMENT_TYPE_SUBMIT_PATIENT_INSTRUCTIONS]: 'counseling',
};

const EHRTemplateMapping = () => {
  const [encounterPhase] = useState('in-visit');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sections, setSections] = useState([]);

  const {
    data: currentPractitionerSettings,
    isLoading: currentPractitionerSettingsLoading,
  } = useGetPractitionerSettings();
  const { mutateAsync: updateSettings, isPending: isSaving } =
    useUpdatePractitionerSettings();

  // Load existing mappings when template is selected and settings are loaded
  useEffect(() => {
    if (
      selectedTemplate &&
      selectedTemplate.body &&
      selectedTemplate.body.sections
    ) {
      // Start with fresh copy of template sections
      const newSections = JSON.parse(
        JSON.stringify(selectedTemplate.body.sections)
      );

      const settings =
        currentPractitionerSettings?.note_preferences?.op_ehr_push_mapping;

      if (settings) {
        const templateId =
          selectedTemplate.template_overrides_id ||
          selectedTemplate.template_id;

        let savedMapping = null;

        // Check if it's a map keyed by ID (Dictionary structure) which is what we want now
        if (settings[templateId]) {
          savedMapping = settings[templateId].mapping || settings[templateId];
        }
        // Fallback: Check if it matches the old single-object structure
        else if (settings.template_id === templateId && settings.mapping) {
          savedMapping = settings.mapping;
        }

        if (savedMapping && savedMapping.sections) {
          // Map saved settings to sections
          newSections.forEach((section) => {
            // Try to find matching section in saved mapping
            const savedSection = savedMapping.sections.find(
              (s) => s.name === section.name
            );

            if (savedSection) {
              section.op_mapping = savedSection.op_mapping;
              section.allow_push = savedSection.allow_push;
            }
          });
        }
      }

      setSections(newSections);
    } else {
      setSections([]);
    }
  }, [selectedTemplate, currentPractitionerSettings]);

  const handleMappingChange = (index, value) => {
    const newSections = [...sections];
    newSections[index].op_mapping = value;
    setSections(newSections);
  };

  const handlePushToggle = (index, checked) => {
    const newSections = [...sections];
    newSections[index].allow_push = checked;
    setSections(newSections);
  };

  const handleSaveMappings = async () => {
    if (
      !selectedTemplate ||
      (!selectedTemplate.template_id && !selectedTemplate.template_overrides_id)
    ) {
      return;
    }

    try {
      const templateId =
        selectedTemplate.template_overrides_id || selectedTemplate.template_id;

      const mappingData = {
        template_name: selectedTemplate.name,
        sections: sections.map((s) => ({
          name: s.name,
          op_mapping: s.op_mapping || null,
          allow_push: s.allow_push !== false,
        })),
      };

      const existingMappings =
        currentPractitionerSettings?.note_preferences?.op_ehr_push_mapping ||
        {};

      const payload = {
        op_ehr_push_mapping: {
          ...existingMappings,
          [templateId]: {
            mapping: mappingData,
          },
        },
      };

      await updateSettings({ settings: payload });
      showToastSuccess('Mappings saved successfully');
    } catch (error) {
      console.error('Error saving mappings:', error);
    }
  };

  return (
    <div className="ehr-template-mapping">
      <Card className="shadow-md bg-white rounded-xl mb-4">
        <div className="mb-6">
          <Title level={4}>OP Template Mapping</Title>
          <Text type="secondary">
            Map Opie AI template sections to OP template sections
          </Text>
        </div>

        <div className="mb-6">
          <Title level={5}>Step 1: Select Template</Title>
          <Text strong className="block mb-2">
            Select Template
          </Text>
          <TemplateSelectDropdown
            encounterPhase={encounterPhase}
            currentTemplate={selectedTemplate}
            setCurrentTemplate={setSelectedTemplate}
            isControlled={true}
          />
        </div>
      </Card>

      {currentPractitionerSettingsLoading && !selectedTemplate ? (
        <Card className="shadow-md bg-white rounded-xl">
          <Skeleton active />
        </Card>
      ) : (
        selectedTemplate &&
        sections.length > 0 && (
          <Card className="shadow-md bg-white rounded-xl">
            <div className="flex justify-between items-center mb-6">
              <Title level={5} className="m-0">
                Step 2: Map Template Sections
              </Title>
              <Button
                type="primary"
                className="ih-button-primary bg-[#00D09C] border-[#00D09C] hover:bg-[#00b588] hover:border-[#00b588]"
                onClick={handleSaveMappings}
                loading={isSaving}
              >
                Save Mappings
              </Button>
            </div>

            <Text type="secondary" className="block mb-6">
              Map each Opie AI section to the corresponding OP template section
            </Text>

            <div className="mapping-list">
              <Row
                gutter={[16, 16]}
                className="mb-2 font-medium text-gray-500 hidden md:flex"
              >
                <Col span={8}>Opie AI Section</Col>
                <Col span={2} className="text-center"></Col>
                <Col span={10}>OP Template Section</Col>
                <Col span={4} className="text-right">
                  Allow Push
                </Col>
              </Row>

              {sections.map((section, index) => (
                <div
                  key={index}
                  className="mb-4 p-4 border rounded-lg bg-gray-50"
                >
                  <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={8}>
                      <div className="font-medium bg-gray-200 px-3 py-2 rounded inline-block w-full md:w-auto">
                        {section.name}
                      </div>
                    </Col>

                    <Col xs={24} md={2} className="flex justify-center">
                      <ArrowRightOutlined className="text-gray-400 transform rotate-90 md:rotate-0 my-2 md:my-0" />
                    </Col>

                    <Col xs={24} md={10}>
                      <Select
                        className="w-full"
                        placeholder="Select OP section..."
                        value={section.op_mapping}
                        onChange={(value) => handleMappingChange(index, value)}
                        allowClear
                      >
                        {Object.entries(OP_FIELD_MAPPING).map(
                          ([key, value]) => (
                            <Option key={key} value={value}>
                              {value}
                            </Option>
                          )
                        )}
                      </Select>
                    </Col>

                    <Col
                      xs={24}
                      md={4}
                      className="flex justify-between md:justify-end items-center"
                    >
                      <span className="md:hidden mr-2">Allow Push</span>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={section.allow_push !== false} // Default to true/enabled if undefined? Screenshot shows "Enabled"
                          onChange={(checked) =>
                            handlePushToggle(index, checked)
                          }
                          className="bg-gray-300"
                        />
                        <span>
                          {section.allow_push !== false
                            ? 'Enabled'
                            : 'Disabled'}
                        </span>
                      </div>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          </Card>
        )
      )}
    </div>
  );
};

export default EHRTemplateMapping;
